import {Client} from '@stomp/stompjs';
import SockJS from 'sockjs-client';

const BACKEND_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:8443';

export const registerDevice = async (fingerprint, deviceInfo = null) => {
    try {
        const response = await fetch(`${BACKEND_BASE_URL}/api/device/register`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                fingerprint,
                deviceInfo,
                timestamp: new Date().toISOString()
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error registering device:', error);
        throw error;
    }
};

export const getViewForDevice = async (fingerprint) => {
    try {
        const response = await fetch(`${BACKEND_BASE_URL}/api/device/${fingerprint}/view`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting view for device:', error);
        throw error;
    }
};

export const setViewForDevice = async (fingerprint, viewName) => {
    try {
        const response = await fetch(`${BACKEND_BASE_URL}/api/device/${fingerprint}/view`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ viewName }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error setting view for device:', error);
        throw error;
    }
};

export const loadAvailableViews = async () => {
    try {
        const response = await fetch(`${BACKEND_BASE_URL}/api/device/views/available`);
        const data = await response.json();

        if (data.status === 'success') {
            const filteredViews = Object.entries(data.views)
                .filter(([key]) => !['device-identifier', 'error'].includes(key))
                .reduce((obj, [key, value]) => {
                    obj[key] = value;
                    return obj;
                }, {});

            return filteredViews;
        } else {
            throw new Error('Invalid response status');
        }
    } catch (error) {
        console.error('Error loading available views:', error);
        return {
            'default': 'Widok domyślny',
            'game': 'Widok gry',
            'leaderboard': 'Tablica wyników',
            'admin': 'Panel administratora'
        };
    }
};

class MiniGolfAPI {
    constructor(baseURL = BACKEND_BASE_URL) {
        this.baseURL = baseURL;
        this.stompClient = null;
    }

    initializeSocket(onGameStateUpdate) {
        try {
            const socket = new SockJS(`${this.baseURL}/ws`);

            this.stompClient = new Client({
                webSocketFactory: () => socket,

                debug: (str) => {
                    if (process.env.NODE_ENV === 'development') {
                        console.log('🔌 STOMP:', str);
                    }
                },

                onConnect: (frame) => {
                    console.log('✅ Połączono z serwerem');

                    this.stompClient.subscribe('/topic/gameState', (message) => {
                        console.log('📊 Aktualizacja stanu gry');
                        try {
                            const gameState = JSON.parse(message.body);
                            onGameStateUpdate(gameState);
                        } catch (error) {
                            console.error('❌ Błąd parsowania gameState:', error);
                        }
                    });

                    this.stompClient.subscribe('/topic/players', (message) => {
                        console.log('👥 Aktualizacja grup graczy');
                        try {
                            const playersUpdate = JSON.parse(message.body);
                            onGameStateUpdate({players: playersUpdate});
                        } catch (error) {
                            console.error('❌ Błąd parsowania players:', error);
                        }
                    });

                    this.stompClient.subscribe('/topic/scores', (message) => {
                        console.log('🏆 Aktualizacja wyników');
                        try {
                            const scoresUpdate = JSON.parse(message.body);
                            onGameStateUpdate(scoresUpdate);
                        } catch (error) {
                            console.error('❌ Błąd parsowania scores:', error);
                        }
                    });
                },

                onDisconnect: () => {
                    console.log('❌ Rozłączono z serwerem');
                },

                onStompError: (frame) => {
                    console.error('❌ Błąd STOMP:', frame.headers?.message || 'Unknown error');
                    console.error('Szczegóły:', frame.body);
                },

                onWebSocketError: (error) => {
                    console.error('❌ Błąd WebSocket:', error);
                },

                reconnectDelay: 5000,
                heartbeatIncoming: 4000,
                heartbeatOutgoing: 4000
            });

            this.stompClient.activate();

        } catch (error) {
            console.error('❌ Błąd inicjalizacji WebSocket:', error);
            this.initializePolling(onGameStateUpdate);
        }
    }

    initializePolling(onGameStateUpdate) {
        console.log('📡 Przełączanie na HTTP polling...');

        this.pollingInterval = setInterval(async () => {
            try {
                const players = await this.getPlayers();
                onGameStateUpdate({players});
            } catch (error) {
                console.error('❌ Błąd polling:', error);
            }
        }, 3000);
    }

    async fetchJSON(url, options = {}) {
        try {
            const response = await fetch(`${this.baseURL}${url}`, {
                headers: {
                    'Content-Type': 'application/json',
                    ...options.headers
                },
                ...options
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({error: 'Błąd serwera'}));
                throw new Error(error.error || 'Błąd serwera');
            }

            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    }

    async getAvailableAvatars() {
        return this.fetchJSON('/api/avatars');
    }

    async getPlayers() {
        return this.fetchJSON('/api/players');
    }

    async getHoles() {
        return this.fetchJSON('/api/holes');
    }

    async loginPlayer(nfcTagId, avatarId, groupName, playerNames) {
        console.log('🎯 Logowanie grupy:', {nfcTagId, avatarId, groupName, playerNames});

        const result = await this.fetchJSON('/api/login', {
            method: 'POST',
            body: JSON.stringify({
                nfcTagId,
                avatarId,
                groupName,
                playerNames
            })
        });

        if (this.stompClient && this.stompClient.connected) {
            try {
                this.stompClient.publish({
                    destination: '/app/playerLogin',
                    body: JSON.stringify({
                        nfcTagId,
                        avatarId,
                        groupName,
                        playerNames,
                        result
                    })
                });
            } catch (error) {
                console.error('❌ Błąd wysyłania WebSocket message:', error);
            }
        }

        return result;
    }

    async logoutPlayer(nfcTagId) {
        console.log('🚪 Wylogowanie grupy:', nfcTagId);

        const result = await this.fetchJSON('/api/logout', {
            method: 'POST',
            body: JSON.stringify({nfcTagId})
        });

        if (this.stompClient && this.stompClient.connected) {
            try {
                this.stompClient.publish({
                    destination: '/app/playerLogout',
                    body: JSON.stringify({nfcTagId})
                });
            } catch (error) {
                console.error('❌ Błąd wysyłania WebSocket message:', error);
            }
        }

        return result;
    }

    async getPlayer(nfcTagId) {
        return this.fetchJSON(`/api/player/${nfcTagId}`);
    }

    async submitScore(nfcTagId, holeNumber, strokes) {
        console.log('🏌️ Wysyłanie wyniku (stary format):', {nfcTagId, holeNumber, strokes});

        const result = await this.fetchJSON('/api/score', {
            method: 'POST',
            body: JSON.stringify({nfcTagId, holeNumber, strokes})
        });

        if (this.stompClient && this.stompClient.connected) {
            try {
                this.stompClient.publish({
                    destination: '/app/scoreUpdate',
                    body: JSON.stringify({nfcTagId, holeNumber, strokes, score: result})
                });
            } catch (error) {
                console.error('❌ Błąd wysyłania WebSocket message:', error);
            }
        }

        return result;
    }

    async submitGroupScore(scoreRequest) {
        console.log('🏌️‍♂️ Wysyłanie wyników grupy:', scoreRequest);

        const result = await this.fetchJSON('/api/score', {
            method: 'POST',
            body: JSON.stringify(scoreRequest)
        });

        if (this.stompClient && this.stompClient.connected) {
            try {
                this.stompClient.publish({
                    destination: '/app/scoreUpdate',
                    body: JSON.stringify({
                        ...scoreRequest,
                        score: result
                    })
                });
            } catch (error) {
                console.error('❌ Błąd wysyłania WebSocket message:', error);
            }
        }

        return result;
    }

    async getHole(holeId) {
        return this.fetchJSON(`/api/holes/${holeId}`);
    }

    async resetGameState() {
        console.log('🔄 Resetowanie stanu gry...');

        const result = await this.fetchJSON('/api/admin/reset', {
            method: 'POST',
            body: JSON.stringify({
                timestamp: new Date().toISOString(),
                action: 'RESET_GAME_STATE'
            })
        });

        // Wyślij powiadomienie przez WebSocket o resecie gry
        if (this.stompClient && this.stompClient.connected) {
            try {
                this.stompClient.publish({
                    destination: '/app/gameReset',
                    body: JSON.stringify({
                        timestamp: new Date().toISOString(),
                        result
                    })
                });
            } catch (error) {
                console.error('❌ Błąd wysyłania WebSocket message:', error);
            }
        }

        return result;
    }

    disconnect() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }

        if (this.stompClient) {
            try {
                if (this.stompClient.active) {
                    this.stompClient.deactivate();
                }
            } catch (error) {
                console.error('❌ Błąd zamykania WebSocket:', error);
            }
            console.log('🔌 Rozłączono WebSocket');
        }
    }
}

export default MiniGolfAPI;